import { FC, useCallback, useState, useMemo } from 'react'
import { Form, Field } from 'react-final-form'
import { useCountDown } from 'ahooks'
import _ from 'lodash'

// constants
import {
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  BUTTON_ICON_POSITIONS,
} from 'components/common/Button'

// utils
import {
  MFAValues,
  validateMfa,
} from 'components/login/LoginForm/SetupMFA/utils'
import { switchcase } from 'helpers/utils'
import { showError } from 'helpers/message'

// components
import { IconButton, Button } from 'components/common'
import { CodeInput, Toggle } from 'components/common/Form'
import { Title, Subtitle } from 'components/login/common'

// types
import { AuthChallengeType } from 'types/graphql'

import scss from '../index.module.scss'

/** 3s */
const RESENT_INTERVAL = 1000 * 3

const getMFAWording = (mfaType: AuthChallengeType) =>
  switchcase({
    [AuthChallengeType.SoftwareTokenMfa]: 'security app',
    [AuthChallengeType.SmsMfa]: 'text message (SMS)',
  })('')(mfaType)

export interface MFACodeFormProps {
  authChallengeType: AuthChallengeType
  showRememberDevice?: boolean
  submitButtonText?: string
  onResentCode?: () => Promise<void>
  onSubmit: (values: MFAValues & { rememberDevice?: boolean }) => Promise<{
    'FINAL_FORM/form-error': string
  } | null>
}

const MFACodeForm: FC<MFACodeFormProps> = ({
  authChallengeType,
  onResentCode,
  showRememberDevice = false,
  submitButtonText = 'Sign In',
  onSubmit,
}) => {
  const [isSending, setIsSending] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [targetDate, setTargetDate] = useState<number>()
  const [countdown] = useCountDown({
    targetDate,
  })

  const enableCodeResend =
    onResentCode && authChallengeType === AuthChallengeType.SmsMfa

  const handleResentCode = useCallback(async () => {
    if (!onResentCode) {
      showError('Code resending is not supported')
      return
    }

    try {
      setIsSending(true)
      await onResentCode()
      setCodeSent(true)
      setTargetDate(Date.now() + RESENT_INTERVAL)
    } catch (error: unknown) {
      showError('Failed to resend code', {
        description: _.isError(error) ? error.message : '',
      })
    } finally {
      setIsSending(false)
    }
  }, [onResentCode])

  const resendButtonContent = useMemo(() => {
    if (!countdown) {
      return 'Resend'
    }
    if (codeSent) {
      return 'Resent!'
    }
    if (isSending) {
      return 'Resending'
    }

    return ''
  }, [codeSent, countdown, isSending])

  return (
    <>
      <Title data-testid='mfa-type'>2-Step verification</Title>
      <Subtitle>with {getMFAWording(authChallengeType) as string}</Subtitle>

      <Form
        onSubmit={onSubmit}
        validate={values => validateMfa(values)}
        render={({
          handleSubmit,
          submitting,
          submitError,
          hasValidationErrors,
        }) => (
          <form onSubmit={handleSubmit} className={scss.form}>
            {submitError && (
              <div className='alert bg-danger d-flex align-items-center text-white'>
                <IconButton
                  disabled
                  icon='MdWarning'
                  size={16}
                  style={{ color: '#fff' }}
                />
                <div>{submitError}</div>
              </div>
            )}
            <div className={`text-start ${scss.row}`}>
              <Field
                labelClassName='form-label'
                component={CodeInput}
                name='code'
                label='Security code'
                required
                handleSubmit={handleSubmit}
              />
              {enableCodeResend && (
                <>
                  <span className='smallText'>
                    Enter the code sent to your phone.
                  </span>
                  <Button
                    {...(countdown && codeSent && { icon: 'MdCheck' })}
                    variant={BUTTON_VARIANTS.link}
                    disabled={!!(isSending || countdown)}
                    className={countdown && codeSent ? 'text-success' : ''}
                    iconPosition={BUTTON_ICON_POSITIONS.left}
                    isLoading={isSending}
                    onClick={handleResentCode}
                    size={BUTTON_SIZES.small}
                  >
                    {resendButtonContent}
                  </Button>
                </>
              )}
            </div>
            {showRememberDevice && (
              <div className={`text-start ${scss.row}`}>
                <Field
                  component={Toggle}
                  name='rememberDevice'
                  label='Trust this browser'
                  toggleLabel="Don't ask me again on this browser"
                />
              </div>
            )}
            <Button
              type='submit'
              variant={BUTTON_VARIANTS.primary}
              disabled={submitting || hasValidationErrors}
              block
              className='mx-0'
              isIconFixed
              isLoading={submitting}
              icon='ArrowRightNextIcon'
            >
              {submitButtonText}
            </Button>
          </form>
        )}
      />
    </>
  )
}

export default MFACodeForm
